import React, { useState, useEffect, useRef } from 'react'
import { createConsumer } from '@rails/actioncable'
import ChatConversationList from './ChatConversationList'
import ChatMessageList from './ChatMessageList'
import ChatComposer from './ChatComposer'
import { useConversations } from './ConversationContext'

const ChatWidget = ({ currentUser, documentId }) => {
  console.log('ChatWidget render - currentUser:', currentUser)
  console.log('ChatWidget render - documentId:', documentId)

  const [isOpen, setIsOpen] = useState(false)
  const [conversations, setConversations] = useState([])
  const [activeConversation, setActiveConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [unreadTotal, setUnreadTotal] = useState(0)
  const [showConversationList, setShowConversationList] = useState(true)
  const [recipientStatus] = useState(null)

  const consumer = useRef(null)
  const subscription = useRef(null)
  const conversationsSubscription = useRef(null)
  const messageTimeouts = useRef(new Map()) // Track timeouts for optimistic messages
  const accountStatusRef = useRef(null)

  // Check if current user is a consumer
  const isConsumer = currentUser?.is_consumer

  // Use context for consumers, local state for collectors
  const conversationContext = useConversations()
  const contextConversations = isConsumer ? conversationContext?.conversations || [] : []
  const contextUnreadTotal = isConsumer ? conversationContext?.getTotalUnreadCount() || 0 : 0

  // Initialize ActionCable consumer
  useEffect(() => {
    console.log('🔌 Initializing ActionCable consumer...')
    consumer.current = createConsumer()

    return () => {
      if (consumer.current) {
        consumer.current.disconnect()
      }
      // Clean up any pending message timeouts
      messageTimeouts.current.forEach(timeoutId => clearTimeout(timeoutId))
      messageTimeouts.current.clear()
    }
  }, [])

  // Subscribe to global conversations channel for unread count updates (collectors only)
  useEffect(() => {
    if (!isConsumer && currentUser && consumer.current) {
      console.log('📡 Subscribing to ConversationsChannel...')

      conversationsSubscription.current = consumer.current.subscriptions.create(
        { channel: 'ConversationsChannel' },
        {
          connected() {
            console.log('🔗 Connected to ConversationsChannel')
          },
          disconnected() {
            console.log('❌ Disconnected from ConversationsChannel')
          },
          received(data) {
            console.log('📨 ConversationsChannel received:', data)

            if (data.type === 'conversation_updated') {
              // Update the unread count for the specific conversation
              setConversations(prev =>
                prev.map(conv => {
                  if (conv.id === data.conversation_id) {
                    return {
                      ...conv,
                      attributes: {
                        ...conv.attributes,
                        unread_count: data.unread_count,
                      },
                    }
                  }
                  return conv
                })
              )

              // Recalculate total unread
              setConversations(prev => {
                const total = prev.reduce(
                  (sum, conv) => sum + (conv.attributes.unread_count || 0),
                  0
                )
                setUnreadTotal(total)
                return prev
              })
            }
          },
        }
      )
    }

    return () => {
      if (conversationsSubscription.current) {
        conversationsSubscription.current.unsubscribe()
      }
    }
  }, [currentUser, isConsumer])

  // Load conversations on mount (collectors only)
  useEffect(() => {
    if (!isConsumer && currentUser) {
      loadConversations()
    }
  }, [currentUser, isConsumer])

  // Listen for custom events to open chat with specific users
  useEffect(() => {
    const handleOpenChat = event => {
      const { recipientId, documentId, recipientStatus, recipientType } = event.detail

      // Store the account status to use later
      accountStatusRef.current = recipientStatus

      // Open the chat widget
      setIsOpen(true)

      // Create or find conversation with the specific document ID from the event
      createOrFindConversation(recipientId, recipientType, documentId)
    }

    window.addEventListener('openChatWithUser', handleOpenChat)

    return () => {
      window.removeEventListener('openChatWithUser', handleOpenChat)
    }
  }, [])

  // Subscribe to conversation channel when active conversation changes
  useEffect(() => {
    if (activeConversation && consumer.current) {
      // Unsubscribe from previous subscription
      if (subscription.current) {
        subscription.current.unsubscribe()
      }

      // Subscribe to new conversation
      subscription.current = consumer.current.subscriptions.create(
        {
          channel: 'ChatChannel',
          conversation_id: activeConversation.id,
        },
        {
          connected() {
            console.log('🔗 ActionCable connected to conversation:', activeConversation.id)
          },
          disconnected() {
            console.log('❌ ActionCable disconnected from conversation:', activeConversation.id)
          },
          rejected() {
            console.log(
              '🚫 ActionCable subscription rejected for conversation:',
              activeConversation.id
            )
          },
          received(data) {
            console.log('📡 Raw ActionCable data received:', data)
            handleChannelMessage(data)
          },
        }
      )
    }

    return () => {
      if (subscription.current) {
        subscription.current.unsubscribe()
      }
    }
  }, [activeConversation])

  const handleChannelMessage = data => {
    console.log('🚨 ActionCable received:', data)

    if (data.type === 'new_message') {
      const newMessage = data.message
      console.log('📨 Processing new_message:', newMessage)

      // Clear any pending timeout for this message
      if (messageTimeouts.current.has(newMessage.id)) {
        clearTimeout(messageTimeouts.current.get(newMessage.id))
        messageTimeouts.current.delete(newMessage.id)
        console.log('⏰ Cleared timeout for message:', newMessage.id)
      }

      // Check if this message already exists in our array (from optimistic add)
      setMessages(prev => {
        const existingIndex = prev.findIndex(msg => msg.id === newMessage.id)
        console.log('🔍 Found existing message at index:', existingIndex)

        if (existingIndex >= 0) {
          // Update existing optimistic message
          console.log('✅ Updating existing optimistic message')
          const updated = [...prev]
          updated[existingIndex] = {
            ...newMessage,
            attributes: {
              ...newMessage.attributes,
              _optimistic: false, // Remove optimistic flag
            },
          }
          return updated
        } else {
          // This is a genuinely new message from another participant
          console.log('➕ Adding new message from another participant')
          // Ensure no duplicates by filtering out any existing messages with same ID
          const filtered = prev.filter(msg => msg.id !== newMessage.id)
          return [...filtered, newMessage]
        }
      })

      // Update conversation's last message
      setConversations(prev =>
        prev.map(conv => {
          if (
            conv.id === newMessage.relationships?.conversation?.data?.id ||
            conv.id === newMessage.conversation_id
          ) {
            return {
              ...conv,
              attributes: {
                ...conv.attributes,
                last_message: {
                  id: newMessage.id,
                  body: newMessage.attributes.body,
                  created_at: newMessage.attributes.created_at,
                  sender_name:
                    newMessage.attributes.sender?.display_name || newMessage.attributes.sender_name,
                },
                updated_at: newMessage.attributes.created_at,
              },
            }
          }
          return conv
        })
      )
    } else if (data.type === 'message_blocked') {
      // Handle blocked message - update existing message regardless of sender
      console.log('🚫 Processing blocked message:', data.message)
      const blockedMessage = data.message

      // Clear any pending timeout for this message
      if (messageTimeouts.current.has(blockedMessage.id)) {
        clearTimeout(messageTimeouts.current.get(blockedMessage.id))
        messageTimeouts.current.delete(blockedMessage.id)
        console.log('⏰ Cleared timeout for blocked message:', blockedMessage.id)
      }

      setMessages(prev =>
        prev.map(msg =>
          msg.id === blockedMessage.id
            ? {
                ...blockedMessage,
                attributes: {
                  ...blockedMessage.attributes,
                  _optimistic: false, // Remove optimistic flag
                },
              }
            : msg
        )
      )
    }
  }

  const loadConversations = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/v1/conversations', {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        },
        credentials: 'same-origin',
      })

      console.log('Conversations API response:', response.status, response.statusText)

      if (!response.ok) {
        const errorData = await response.text()
        console.error('Conversations API error:', errorData)
        throw new Error('Failed to load conversations')
      }

      const data = await response.json()
      console.log('Conversations data:', data)
      setConversations(data.data || [])

      // Calculate total unread
      const total = (data.data || []).reduce(
        (sum, conv) => sum + (conv.attributes.unread_count || 0),
        0
      )
      setUnreadTotal(total)
    } catch (err) {
      console.error('Error loading conversations:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const loadMessages = async conversationId => {
    try {
      setLoading(true)
      const response = await fetch(`/api/v1/conversations/${conversationId}/messages`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        },
      })

      if (!response.ok) throw new Error('Failed to load messages')

      const data = await response.json()
      // Ensure unique messages by ID when loading
      const uniqueMessages = (data.data || []).filter(
        (message, index, array) => array.findIndex(m => m.id === message.id) === index
      )
      setMessages(uniqueMessages) // Remove reverse() - API should return oldest first
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const selectConversation = async conversation => {
    setActiveConversation(conversation)
    setShowConversationList(false)
    await loadMessages(conversation.id)

    // Note: Messages are now marked as read automatically when they become visible
    // via the Intersection Observer in ChatMessageList

    // Update local unread count optimistically (will be corrected by server updates)
    setConversations(prev =>
      prev.map(conv => {
        if (conv.id === conversation.id) {
          return {
            ...conv,
            attributes: {
              ...conv.attributes,
              unread_count: 0,
            },
          }
        }
        return conv
      })
    )
  }

  // Handle messages being viewed (called by ChatMessageList via Intersection Observer)
  const handleMessagesViewed = messageIds => {
    if (!activeConversation || !subscription.current) return

    // Mark the conversation as read when messages are viewed
    subscription.current.perform('mark_as_read', {
      conversation_id: activeConversation.id,
    })

    console.log('📖 Messages viewed and marked as read:', messageIds)
  }

  const sendMessage = async (body, attachments = []) => {
    if (!activeConversation || (!body.trim() && attachments.length === 0)) return

    console.log('📤 Sending message to conversation:', activeConversation.id)

    try {
      const formData = new FormData()
      formData.append('message[body]', body)

      // Append each attachment
      attachments.forEach((file, _index) => {
        formData.append(`message[attachments][]`, file)
      })

      const response = await fetch(`/api/v1/conversations/${activeConversation.id}/messages`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        },
        body: formData,
      })

      if (!response.ok) throw new Error('Failed to send message')

      const data = await response.json()

      // Optimistically add message with scanning state
      const optimisticMessage = {
        ...data.data,
        attributes: {
          ...data.data.attributes,
          scanned: false,
          _optimistic: true,
        },
      }

      console.log('⏳ Adding optimistic message with scanning state:', optimisticMessage.id)
      setMessages(prev => {
        // Prevent duplicate optimistic messages
        const filtered = prev.filter(msg => msg.id !== optimisticMessage.id)
        return [...filtered, optimisticMessage]
      })

      // Set a fallback timeout to mark message as scanned if ActionCable doesn't respond
      const timeoutId = setTimeout(() => {
        console.log('⚠️ Timeout reached for message, marking as scanned:', optimisticMessage.id)
        setMessages(prev =>
          prev.map(msg =>
            msg.id === optimisticMessage.id
              ? {
                  ...msg,
                  attributes: {
                    ...msg.attributes,
                    scanned: true,
                    _optimistic: false,
                  },
                }
              : msg
          )
        )
        messageTimeouts.current.delete(optimisticMessage.id)
      }, 10000) // 10 second fallback

      messageTimeouts.current.set(optimisticMessage.id, timeoutId)
      console.log('⏰ Set 10s fallback timeout for message:', optimisticMessage.id)
    } catch (err) {
      console.error('Error sending message:', err)
      setError(err.message)
    }
  }

  const createOrFindConversation = async (
    recipientId,
    recipientType = 'user',
    eventDocumentId = null
  ) => {
    try {
      setLoading(true)
      setError(null)

      console.log('🔍 createOrFindConversation called with:', {
        recipientId,
        recipientType,
        eventDocumentId,
      })

      // Use document ID from event if provided, otherwise fall back to prop
      const docId = eventDocumentId || documentId

      // First, check if conversation already exists
      const conversationsResponse = await fetch('/api/v1/conversations', {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        },
        credentials: 'same-origin',
      })

      if (!conversationsResponse.ok) {
        throw new Error('Failed to load conversations')
      }

      const conversationsData = await conversationsResponse.json()
      const currentConversations = conversationsData.data || []
      setConversations(currentConversations)

      // Look for existing conversation with this recipient
      const existingConv = currentConversations.find(conv => {
        const participants = conv.attributes?.other_participants || []

        if (recipientType === 'law_firm') {
          // For law firm recipients, check if any participant is a law firm with matching ID
          return participants.some(p => p.type === 'law_firm' && p.id === recipientId)
        } else {
          // For user recipients - check by user ID
          return participants.some(p => p.type === 'user' && p.id === recipientId)
        }
      })

      console.log('🔍 Existing conversation found:', existingConv?.id)

      if (existingConv) {
        console.log('✅ Selecting existing conversation')
        selectConversation(existingConv)
      } else {
        console.log('➕ Creating new conversation')

        // For collectors authenticated via auth token, we need to create conversation
        // between the law firm and the consumer
        let requestBody

        if (currentUser?.is_law_firm_entity) {
          // Law firm entity creating conversation with consumer
          // The backend will handle this as a law firm to user conversation
          requestBody = {
            recipient_id: recipientId,
            document_id: docId,
          }
        } else if (currentUser?.law_firm_id && !recipientType) {
          // Individual collector user creating conversation with consumer
          requestBody = {
            recipient_id: recipientId,
            document_id: docId,
          }
        } else if (recipientType === 'law_firm') {
          // Consumer creating conversation with law firm
          requestBody = {
            law_firm_id: recipientId,
            document_id: docId,
          }
        } else {
          // Default case - user to user
          requestBody = {
            recipient_id: recipientId,
            document_id: docId,
          }
        }

        console.log('📤 POST request body:', requestBody)

        const response = await fetch('/api/v1/conversations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
          },
          body: JSON.stringify(requestBody),
        })

        console.log('📥 POST response status:', response.status)

        if (!response.ok) {
          const data = await response.json()
          console.error('❌ POST response error:', data)
          throw new Error(data.error || 'Failed to create conversation')
        }

        const data = await response.json()
        console.log('✅ POST response data:', data)

        // Refresh conversations to include the new one
        const updatedConversationsResponse = await fetch('/api/v1/conversations', {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
          },
          credentials: 'same-origin',
        })

        if (updatedConversationsResponse.ok) {
          const updatedConversationsData = await updatedConversationsResponse.json()
          setConversations(updatedConversationsData.data || [])
        }

        selectConversation(data.data)
      }
    } catch (err) {
      console.error('💥 createOrFindConversation error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const createConversation = async recipientId => {
    // This method is for the conversation list UI - always assumes user recipient
    await createOrFindConversation(recipientId, 'user')
  }

  const handleBack = () => {
    setShowConversationList(true)
    setActiveConversation(null)
    setMessages([])
  }

  // Function to get account status for a conversation
  const getAccountStatus = (conversation, fallbackStatus = null) => {
    // If we have a fallback status (from "Send Message" action), use it
    if (fallbackStatus) {
      return fallbackStatus
    }

    // Try to get status from conversation attributes
    if (conversation?.attributes?.account_status) {
      return conversation.attributes.account_status
    }

    // Fallback to user type
    const otherParticipant = conversation?.attributes?.other_participants?.[0]
    return otherParticipant?.is_collector ? 'Collector' : 'Consumer'
  }

  return (
    <>
      {/* Chat launcher button - hide for consumers */}
      {!isConsumer && (
        <button className='chat-launcher' onClick={() => setIsOpen(!isOpen)} aria-label='Open chat'>
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path
              d='M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z'
              fill='white'
            />
          </svg>
          {(isConsumer ? contextUnreadTotal : unreadTotal) > 0 && (
            <span className='chat-unread-badge'>
              {isConsumer ? contextUnreadTotal : unreadTotal}
            </span>
          )}
        </button>
      )}

      {/* Chat widget */}
      {isOpen && (
        <div className={`chat-widget ${isConsumer ? 'chat-widget-consumer' : ''}`}>
          <div className='chat-header'>
            {showConversationList ? (
              <>
                <h3>Messages</h3>
                <button
                  className='chat-close'
                  onClick={() => setIsOpen(false)}
                  aria-label='Close chat'
                >
                  ×
                </button>
              </>
            ) : (
              <>
                <button
                  className='chat-back'
                  onClick={handleBack}
                  aria-label='Back to conversations'
                >
                  ←
                </button>
                <div className='chat-header-info'>
                  <h3>{activeConversation?.attributes.other_participants[0]?.display_name}</h3>
                  {!isConsumer && (
                    <div
                      className='chat-header-status'
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '4px',
                      }}
                    >
                      <span
                        className='status-label'
                        style={{
                          fontSize: '10px',
                          fontWeight: '700',
                          color: '#9ca3af',
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                        }}
                      >
                        STATUS:
                      </span>
                      <span
                        className='status-value'
                        style={{
                          fontSize: '10px',
                          fontWeight: '700',
                          color: '#9ca3af',
                          letterSpacing: '0.8px',
                        }}
                      >
                        {getAccountStatus(activeConversation, recipientStatus)}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  className='chat-close'
                  onClick={() => setIsOpen(false)}
                  aria-label='Close chat'
                >
                  ×
                </button>
              </>
            )}
          </div>

          <div className='chat-body'>
            {error && (
              <div className='chat-error'>
                {error}
                <button onClick={() => setError(null)}>Dismiss</button>
              </div>
            )}

            {showConversationList ? (
              <ChatConversationList
                conversations={isConsumer ? contextConversations : conversations}
                onSelectConversation={selectConversation}
                onCreateConversation={createConversation}
                loading={loading}
                currentUser={currentUser}
              />
            ) : (
              <>
                <ChatMessageList
                  messages={messages}
                  currentUser={currentUser}
                  loading={loading}
                  onMessagesViewed={handleMessagesViewed}
                />
                <ChatComposer onSendMessage={sendMessage} disabled={loading} />
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default ChatWidget
